import React, { useState, useRef, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    Grid,
    Button,
    Chip,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    TextField,
    InputAdornment,
    Tabs,
    Tab,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress,
    Alert,
    Tooltip,
    Collapse,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useParams, useSearchParams } from 'react-router-dom';
import { GoogleMap, LoadScript, Marker, StandaloneSearchBox } from '@react-google-maps/api';
import SearchIcon from '@mui/icons-material/Search';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import axios from 'axios';
import { approveTrendOpportunity, rejectTrendOpportunity, trendActionError } from '../services/trendProxyService';

// ═══════════════════════════════════════════════════════════════════════════
// API CONFIGURATION
// ═══════════════════════════════════════════════════════════════════════════

// whook, reads only. Approve and reject go through mgmt-backend's proxy (trendProxyService).
const API_BASE_URL = process.env.REACT_APP_WHOOK_API_URL || 'https://api.whook.ai';

// ═══════════════════════════════════════════════════════════════════════════
// DUMMY DATA (kept as-is)
// ═══════════════════════════════════════════════════════════════════════════

const neighborhoods = [
    { id: 1, city: 'Los Angeles', neighborhood: 'Downtown', teamLeader: 'John Doe', employees: ['Alice Smith', 'Bob Johnson'], lat: 34.0407, lng: -118.2468 },
    { id: 2, city: 'Los Angeles', neighborhood: 'Hollywood', teamLeader: 'Jane Doe', employees: ['Charlie Brown', 'Dana White'], lat: 34.0928, lng: -118.3287 },
    { id: 3, city: 'San Francisco', neighborhood: 'Mission District', teamLeader: 'Mike Ross', employees: ['Eve Black', 'Frank Green'], lat: 37.7599, lng: -122.4148 },
    { id: 4, city: 'San Francisco', neighborhood: 'Nob Hill', teamLeader: 'Rachel Adams', employees: ['Grace Lee', 'Henry Scott'], lat: 37.7930, lng: -122.4167 },
];

const employeesList = [
    'Alice Smith', 'Bob Johnson', 'Charlie Brown', 'Dana White', 'Eve Black',
    'Frank Green', 'Grace Lee', 'Henry Scott', 'Olivia Harris', 'Noah Martin',
    'Sophia Brown', 'Liam Wilson', 'Isabella Clark', 'Lucas King',
    'Emma Thomas', 'Mason Lee'
];

const cityMapConfig = {
    'Los Angeles': { lat: 34.0522, lng: -118.2437 },
    'San Francisco': { lat: 37.7749, lng: -122.4194 },
    'New York City': { lat: 40.7128, lng: -74.0060 },
    'Miami': { lat: 25.7617, lng: -80.1918 },
    'Austin': { lat: 30.2672, lng: -97.7431 },
};

// ═══════════════════════════════════════════════════════════════════════════
// STYLED COMPONENTS
// ═══════════════════════════════════════════════════════════════════════════

const PageContainer = styled(Box)({
    padding: '2rem',
    backgroundColor: '#1c1c1c',
    minHeight: '100vh',
    color: '#fff',
    paddingBottom: '6rem',
});

const Title = styled(Typography)({
    fontSize: '2rem',
    fontWeight: 'bold',
    marginBottom: '1rem',
});

const LocationCard = styled(Paper)({
    padding: '1.5rem',
    backgroundColor: '#282828',
    color: '#fff',
    borderRadius: '12px',
    boxShadow: '0px 6px 15px rgba(0, 0, 0, 0.4)',
});

const EmployeeChip = styled(Chip)({
    margin: '5px',
    backgroundColor: '#404040',
    color: '#fff',
});

const Footer = styled(Box)({
    padding: '1rem',
    textAlign: 'center',
    color: '#fff',
    backgroundColor: 'transparent',
    position: 'fixed',
    bottom: 0,
    left: 0,
    width: '100%',
});

const StyledTabs = styled(Tabs)({
    marginBottom: '20px',
    '& .MuiTab-root': {
        color: '#888',
        '&.Mui-selected': {
            color: '#12c099',
        },
    },
    '& .MuiTabs-indicator': {
        backgroundColor: '#12c099',
    },
});

const OpportunityCard = styled(Paper)({
    backgroundColor: '#282828',
    padding: '16px',
    marginBottom: '12px',
    borderRadius: '8px',
    border: '1px solid #333',
    '&:hover': {
        borderColor: '#12c099',
    },
});

const ScoreChip = styled(Chip)(({ score }) => ({
    backgroundColor: score >= 80 ? '#2e7d32' : score >= 60 ? '#f57c00' : '#d32f2f',
    color: '#fff',
    fontWeight: 'bold',
}));

const DemandChip = styled(Chip)(({ demandtype }) => ({
    backgroundColor:
        demandtype === 'proven' ? '#1976d2' :
            demandtype === 'emerging' ? '#9c27b0' :
                demandtype === 'established' ? '#ed6c02' : '#666',
    color: '#fff',
    fontSize: '0.75rem',
}));

// ═══════════════════════════════════════════════════════════════════════════
// REJECTION REASONS
// ═══════════════════════════════════════════════════════════════════════════

const REJECTION_REASONS = [
    { value: 'noise_daily_spike', label: 'Daily Spike / Noise', description: 'Regular season game, daily news cycle' },
    { value: 'noise_weather', label: 'Weather Related', description: 'Weather-driven search spike' },
    { value: 'not_experiential', label: 'Not Experiential', description: "Can't be turned into an experience" },
    { value: 'wrong_timing', label: 'Wrong Timing', description: 'Off-season, past event' },
    { value: 'wrong_market', label: 'Wrong Market', description: 'Not relevant to this city' },
    { value: 'low_demand', label: 'Low Demand', description: 'Not enough interest' },
    { value: 'too_competitive', label: 'Too Competitive', description: 'Market saturated' },
    { value: 'operational_impossible', label: 'Operationally Impossible', description: "Can't execute (no venues, instructors)" },
    { value: 'duplicate', label: 'Duplicate', description: 'Already have similar opportunity' },
    { value: 'other', label: 'Other', description: 'Other reason' },
];

// ═══════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

const CityOverview = () => {
    const { city } = useParams();
    const [searchParams] = useSearchParams();
    const decodedCity = decodeURIComponent(city);

    // Existing state
    const [locationData, setLocationData] = useState(neighborhoods);
    const [selectedEmployee, setSelectedEmployee] = useState('');
    const [assigningNeighborhood, setAssigningNeighborhood] = useState(null);
    const [mapCenter, setMapCenter] = useState(cityMapConfig[decodedCity] || { lat: 34.0522, lng: -118.2437 });
    const [searchBox, setSearchBox] = useState(null);

    // NEW: Tab state (check URL param for initial tab)
    const initialTab = searchParams.get('tab') === 'opportunities' ? 1 : 0;
    const [activeTab, setActiveTab] = useState(initialTab);

    // NEW: Opportunities state
    const [opportunities, setOpportunities] = useState([]);
    const [opportunitiesLoading, setOpportunitiesLoading] = useState(false);
    const [opportunitiesError, setOpportunitiesError] = useState(null);
    const [expandedOpportunities, setExpandedOpportunities] = useState({});

    // NEW: Rejection dialog state
    const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
    const [selectedOpportunity, setSelectedOpportunity] = useState(null);
    const [rejectionReason, setRejectionReason] = useState('');
    const [rejectionExplanation, setRejectionExplanation] = useState('');
    const [actionLoading, setActionLoading] = useState(false);

    // ═══════════════════════════════════════════════════════════════════════
    // FETCH OPPORTUNITIES
    // ═══════════════════════════════════════════════════════════════════════

    const fetchOpportunities = async () => {
        setOpportunitiesLoading(true);
        setOpportunitiesError(null);
        try {
            const response = await axios.get(`${API_BASE_URL}/v1/agent/discovery/opportunities`, {
                params: {
                    city: decodedCity,
                    status: 'pending_review',
                    sortBy: 'opportunityScore',
                    sortOrder: 'desc',
                    limit: 50
                }
            });
            setOpportunities(response.data.data || []);
        } catch (err) {
            console.error('Failed to fetch opportunities:', err);
            setOpportunitiesError(err.response?.data?.error || 'Failed to load opportunities');
        } finally {
            setOpportunitiesLoading(false);
        }
    };

    useEffect(() => {
        if (activeTab === 1) {
            fetchOpportunities();
        }
    }, [activeTab, decodedCity]);

    // ═══════════════════════════════════════════════════════════════════════
    // OPPORTUNITY HANDLERS
    // ═══════════════════════════════════════════════════════════════════════

    const handleApprove = async (opportunity) => {
        setActionLoading(true);
        try {
            await approveTrendOpportunity(opportunity._id);
            setOpportunities(prev => prev.filter(o => o._id !== opportunity._id));
        } catch (err) {
            console.error('Failed to approve:', err);
            alert(trendActionError(err, 'Failed to approve opportunity'));
        } finally {
            setActionLoading(false);
        }
    };

    const openRejectDialog = (opportunity) => {
        setSelectedOpportunity(opportunity);
        setRejectionReason('');
        setRejectionExplanation('');
        setRejectDialogOpen(true);
    };

    const handleReject = async () => {
        if (!rejectionReason) {
            alert('Please select a rejection reason');
            return;
        }

        setActionLoading(true);
        try {
            await rejectTrendOpportunity(selectedOpportunity._id, {
                reason: rejectionReason,
                explanation: rejectionExplanation,
                shouldTeachAgent: true
            });
            setOpportunities(prev => prev.filter(o => o._id !== selectedOpportunity._id));
            setRejectDialogOpen(false);
        } catch (err) {
            console.error('Failed to reject:', err);
            alert(trendActionError(err, 'Failed to reject opportunity'));
        } finally {
            setActionLoading(false);
        }
    };

    const toggleOpportunityExpand = (id) => {
        setExpandedOpportunities(prev => ({ ...prev, [id]: !prev[id] }));
    };

    // ═══════════════════════════════════════════════════════════════════════
    // EXISTING HANDLERS (unchanged)
    // ═══════════════════════════════════════════════════════════════════════

    const handleAssignEmployee = () => {
        if (selectedEmployee && assigningNeighborhood !== null) {
            const newNeighborhoods = locationData.map((neighborhood) => {
                if (neighborhood.id === assigningNeighborhood) {
                    return { ...neighborhood, employees: [...neighborhood.employees, selectedEmployee] };
                }
                return neighborhood;
            });
            setSelectedEmployee('');
            setAssigningNeighborhood(null);
            setLocationData(newNeighborhoods);
        }
    };

    const handleRemoveEmployee = (neighborhoodId, employeeName) => {
        const newNeighborhoods = locationData.map((neighborhood) => {
            if (neighborhood.id === neighborhoodId) {
                return {
                    ...neighborhood,
                    employees: neighborhood.employees.filter((employee) => employee !== employeeName),
                };
            }
            return neighborhood;
        });
        setLocationData(newNeighborhoods);
    };

    const onLoad = (ref) => setSearchBox(ref);

    const onPlacesChanged = () => {
        const places = searchBox.getPlaces();
        if (places.length > 0) {
            const place = places[0];
            setMapCenter({
                lat: place.geometry.location.lat(),
                lng: place.geometry.location.lng(),
            });
        }
    };

    const filteredNeighborhoods = locationData.filter(neighborhood => neighborhood.city === decodedCity);

    // ═══════════════════════════════════════════════════════════════════════
    // RENDER
    // ═══════════════════════════════════════════════════════════════════════

    return (
        <PageContainer>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Title>{decodedCity} Overview</Title>
                {opportunities.length > 0 && (
                    <Chip
                        icon={<TrendingUpIcon />}
                        label={`${opportunities.length} Opportunities`}
                        sx={{ backgroundColor: '#9c27b0', color: '#fff' }}
                    />
                )}
            </Box>

            {/* TABS */}
            <StyledTabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
                <Tab label="Neighborhoods" />
                <Tab
                    label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <TrendingUpIcon fontSize="small" />
                            Trend Opportunities
                            {opportunities.length > 0 && (
                                <Chip
                                    label={opportunities.length}
                                    size="small"
                                    sx={{ backgroundColor: '#9c27b0', color: '#fff', height: 20 }}
                                />
                            )}
                        </Box>
                    }
                />
            </StyledTabs>

            {/* TAB 0: Neighborhoods (Original Content) */}
            {activeTab === 0 && (
                <>
                    <LoadScript googleMapsApiKey={process.env.REACT_APP_GOOGLE_API_KEY} libraries={['places']}>
                        <StandaloneSearchBox onLoad={onLoad} onPlacesChanged={onPlacesChanged}>
                            <TextField
                                variant="outlined"
                                placeholder="Search locations"
                                sx={{
                                    width: '100%',
                                    marginBottom: '1rem',
                                    backgroundColor: '#fff',
                                    color: '#000',
                                    zIndex: 1,
                                    position: 'relative',
                                }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </StandaloneSearchBox>
                        <GoogleMap
                            mapContainerStyle={{ width: '100%', height: '400px', marginBottom: '20px' }}
                            center={mapCenter}
                            zoom={12}
                            options={{
                                tilt: 45,
                                heading: 90,
                                zoomControl: true,
                                streetViewControl: false,
                                fullscreenControl: false,
                                disableDefaultUI: true,
                            }}
                        >
                            {filteredNeighborhoods.map((neighborhood) => (
                                <Marker
                                    key={neighborhood.id}
                                    position={{ lat: neighborhood.lat, lng: neighborhood.lng }}
                                    title={neighborhood.neighborhood}
                                />
                            ))}
                        </GoogleMap>
                    </LoadScript>
                    <Grid container spacing={4}>
                        {filteredNeighborhoods.map((neighborhood) => (
                            <Grid item xs={12} md={6} key={neighborhood.id}>
                                <LocationCard>
                                    <Typography variant="h5" gutterBottom>
                                        {neighborhood.neighborhood}
                                    </Typography>
                                    <Typography variant="subtitle1" gutterBottom>
                                        Team Leader: {neighborhood.teamLeader}
                                    </Typography>
                                    <Box sx={{ marginBottom: '1rem' }}>
                                        <Typography variant="subtitle2">Employees:</Typography>
                                        {neighborhood.employees.map((employee) => (
                                            <EmployeeChip
                                                key={employee}
                                                label={employee}
                                                onDelete={() => handleRemoveEmployee(neighborhood.id, employee)}
                                            />
                                        ))}
                                    </Box>
                                    <FormControl fullWidth variant="outlined" sx={{ marginBottom: '1rem', backgroundColor: '#202020' }}>
                                        <InputLabel sx={{ color: '#12c099' }}>Assign Employee</InputLabel>
                                        <Select
                                            value={selectedEmployee}
                                            onChange={(e) => setSelectedEmployee(e.target.value)}
                                            label="Assign Employee"
                                            sx={{
                                                color: '#fff',
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#12c099',
                                                },
                                            }}
                                        >
                                            {employeesList
                                                .filter((employee) => !neighborhood.employees.includes(employee))
                                                .map((employee) => (
                                                    <MenuItem key={employee} value={employee}>
                                                        {employee}
                                                    </MenuItem>
                                                ))}
                                        </Select>
                                    </FormControl>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={() => {
                                            setAssigningNeighborhood(neighborhood.id);
                                            handleAssignEmployee();
                                        }}
                                        sx={{
                                            backgroundColor: '#12c099',
                                            color: '#fff',
                                            '&:hover': {
                                                backgroundColor: '#0fa77a',
                                            },
                                        }}
                                        fullWidth
                                    >
                                        Assign Employee
                                    </Button>
                                </LocationCard>
                            </Grid>
                        ))}
                    </Grid>
                </>
            )}

            {/* TAB 1: Trend Opportunities */}
            {activeTab === 1 && (
                <Box>
                    {/* Header with refresh */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6" sx={{ color: '#12c099' }}>
                            Pending Review for {decodedCity}
                        </Typography>
                        <IconButton onClick={fetchOpportunities} sx={{ color: '#12c099' }}>
                            <RefreshIcon />
                        </IconButton>
                    </Box>

                    {/* Loading / Error */}
                    {opportunitiesLoading && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress sx={{ color: '#12c099' }} />
                        </Box>
                    )}

                    {opportunitiesError && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {opportunitiesError}
                        </Alert>
                    )}

                    {/* Opportunities List */}
                    {!opportunitiesLoading && !opportunitiesError && (
                        <>
                            {opportunities.map((opp) => (
                                <OpportunityCard key={opp._id}>
                                    {/* Header Row */}
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                        <Box sx={{ flex: 1 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                                <Typography variant="h6" sx={{ color: '#fff' }}>
                                                    {opp.trend}
                                                </Typography>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => toggleOpportunityExpand(opp._id)}
                                                    sx={{ color: '#888' }}
                                                >
                                                    {expandedOpportunities[opp._id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                                </IconButton>
                                            </Box>
                                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                                <ScoreChip label={`Score: ${opp.opportunityScore}`} size="small" score={opp.opportunityScore} />
                                                <DemandChip label={opp.signals?.demandType || 'unknown'} size="small" demandtype={opp.signals?.demandType} />
                                                <Chip label={opp.trendType} size="small" sx={{ backgroundColor: '#444', color: '#fff' }} />
                                                {opp.timing?.urgency === 'immediate' && (
                                                    <Chip label="🔥 URGENT" size="small" sx={{ backgroundColor: '#d32f2f', color: '#fff' }} />
                                                )}
                                            </Box>
                                        </Box>

                                        {/* Action Buttons */}
                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                            <Tooltip title="Approve & Create Experience">
                                                <IconButton
                                                    onClick={() => handleApprove(opp)}
                                                    disabled={actionLoading}
                                                    sx={{ color: '#4caf50' }}
                                                >
                                                    <CheckCircleIcon />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Reject & Teach AI">
                                                <IconButton
                                                    onClick={() => openRejectDialog(opp)}
                                                    disabled={actionLoading}
                                                    sx={{ color: '#f44336' }}
                                                >
                                                    <CancelIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </Box>

                                    {/* Context */}
                                    <Typography variant="body2" sx={{ color: '#aaa', mb: 1 }}>
                                        {opp.trendContext}
                                    </Typography>

                                    {/* TikTok Stats */}
                                    {opp.signals?.tiktokViews && (
                                        <Typography variant="body2" sx={{ color: '#888', mb: 1 }}>
                                            📱 TikTok: {opp.signals.tiktokViews.toLocaleString()} views | {opp.signals.tiktokConfidence}% confidence
                                        </Typography>
                                    )}

                                    {/* Expanded Content */}
                                    <Collapse in={expandedOpportunities[opp._id]}>
                                        <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #444' }}>
                                            {/* Experience Ideas */}
                                            {opp.experienceIdeas?.length > 0 && (
                                                <Box sx={{ mb: 2 }}>
                                                    <Typography variant="subtitle2" sx={{ color: '#12c099', mb: 1 }}>
                                                        Experience Ideas:
                                                    </Typography>
                                                    <Grid container spacing={1}>
                                                        {opp.experienceIdeas.map((idea, idx) => (
                                                            <Grid item xs={12} md={6} key={idx}>
                                                                <Paper sx={{ p: 1.5, backgroundColor: '#333' }}>
                                                                    <Typography variant="body2" sx={{ color: '#fff', fontWeight: 'bold' }}>
                                                                        {idea.name}
                                                                    </Typography>
                                                                    <Typography variant="caption" sx={{ color: '#888' }}>
                                                                        {idea.format} • {idea.venueType}
                                                                    </Typography>
                                                                    {idea.description && (
                                                                        <Typography variant="body2" sx={{ color: '#aaa', mt: 0.5, fontSize: '0.75rem' }}>
                                                                            {idea.description}
                                                                        </Typography>
                                                                    )}
                                                                </Paper>
                                                            </Grid>
                                                        ))}
                                                    </Grid>
                                                </Box>
                                            )}

                                            {/* Reasoning */}
                                            <Typography variant="body2" sx={{ color: '#666', fontStyle: 'italic' }}>
                                                <strong>AI Reasoning:</strong> {opp.reasoning}
                                            </Typography>

                                            {/* Quick Launch Button */}
                                            <Button
                                                variant="contained"
                                                startIcon={<RocketLaunchIcon />}
                                                sx={{
                                                    mt: 2,
                                                    backgroundColor: '#12c099',
                                                    color: '#000',
                                                    '&:hover': { backgroundColor: '#0fa77a' }
                                                }}
                                                onClick={() => handleApprove(opp)}
                                            >
                                                Approve & Start Planning
                                            </Button>
                                        </Box>
                                    </Collapse>
                                </OpportunityCard>
                            ))}

                            {opportunities.length === 0 && (
                                <Box sx={{ textAlign: 'center', py: 4 }}>
                                    <Typography sx={{ color: '#888', mb: 2 }}>
                                        No opportunities pending review for {decodedCity}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: '#666' }}>
                                        Run the Trend Radar to discover new opportunities
                                    </Typography>
                                </Box>
                            )}
                        </>
                    )}
                </Box>
            )}

            {/* Rejection Dialog */}
            <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ backgroundColor: '#282828', color: '#fff' }}>
                    Reject: {selectedOpportunity?.trend}
                </DialogTitle>
                <DialogContent sx={{ backgroundColor: '#282828' }}>
                    <Typography variant="body2" sx={{ color: '#aaa', mb: 2 }}>
                        Your feedback helps the AI avoid similar mistakes in the future.
                    </Typography>
                    <FormControl fullWidth sx={{ mt: 1 }}>
                        <InputLabel sx={{ color: '#12c099' }}>Rejection Reason *</InputLabel>
                        <Select
                            value={rejectionReason}
                            onChange={(e) => setRejectionReason(e.target.value)}
                            label="Rejection Reason *"
                            sx={{ color: '#fff', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#12c099' } }}
                        >
                            {REJECTION_REASONS.map((r) => (
                                <MenuItem key={r.value} value={r.value}>
                                    <Box>
                                        <Typography>{r.label}</Typography>
                                        <Typography variant="caption" sx={{ color: '#888' }}>{r.description}</Typography>
                                    </Box>
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label="Additional Explanation (optional)"
                        value={rejectionExplanation}
                        onChange={(e) => setRejectionExplanation(e.target.value)}
                        sx={{
                            mt: 2,
                            '& .MuiInputBase-input': { color: '#fff' },
                            '& .MuiOutlinedInput-notchedOutline': { borderColor: '#12c099' },
                            '& .MuiInputLabel-root': { color: '#12c099' }
                        }}
                        placeholder="e.g., Regular season Lakers game, not playoff-worthy. Only flag during playoffs."
                    />
                </DialogContent>
                <DialogActions sx={{ backgroundColor: '#282828', p: 2 }}>
                    <Button onClick={() => setRejectDialogOpen(false)} sx={{ color: '#888' }}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleReject}
                        disabled={actionLoading || !rejectionReason}
                        variant="contained"
                        sx={{ backgroundColor: '#f44336', color: '#fff', '&:hover': { backgroundColor: '#d32f2f' } }}
                    >
                        {actionLoading ? <CircularProgress size={20} /> : 'Reject & Teach AI'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Footer>
                <Typography variant="body2">
                    &copy; {new Date().getFullYear()} Mars Reel Media Corp. All rights reserved.
                </Typography>
            </Footer>
        </PageContainer>
    );
};

export default CityOverview;